/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import {FC} from 'react'
import {Routes, Route, BrowserRouter, Navigate} from 'react-router-dom'
import {PrivateRoutes} from './PrivateRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, AuthPage, useAuth} from '../modules/auth'
import {App} from '../App'
import {AdminRoutes} from '../modules/admin/AdminRoutes'
import {RateSupportPage} from '../pages/support/RateSupportPage'
import {CustomerPortal} from '../pages/customer-portal/CustomerPortal'
import ConnectProfilePage from '../pages/public/ConnectProfilePage'
import ProfileLoginEmbed from '../pages/public/ProfileLoginEmbed'
import ConnectMobilePage from '../pages/public/connect-mobile/ConnectMobilePage'
import PostUploadPage from '../pages/public/PostUploadPage'
import TelegramConnect from '../pages/public/TelegramConnect'
import TestimonialsPage from '../pages/public/TestimonialsPage'
import {TutorialDemo} from '../pages/tutorial-demo/TutorialDemo'
import {TutorialGate} from '../pages/tutorial-demo/TutorialGate'
// @ts-ignore — plain JSX module (ported from the standalone landing site)
import LandingPage from '../pages/landing/LandingPage'
import {TermsWrapper} from '../pages/terms/TermsWrapper'
import {PrivacyWrapper} from '../pages/privacy/PrivacyWrapper'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {PUBLIC_URL} = process.env

const AppRoutes: FC = () => {
  const {currentUser, getBot} = useAuth()

  const params = new URLSearchParams(window.location.search);
  var token = params.get('overide-token');
  if (token) {
    setTimeout(() => {
      localStorage.setItem('kt-auth-react-v', JSON.stringify({api_token: token}));

      const url = window.location.protocol + "//" + window.location.host + window.location.pathname;

      // Redirect to the constructed URL
      window.location.href = url;
    }, 1000)
  }

  // An ?onboarding= link opened while logged out bounces to /auth and loses its query
  // string. Stash the value so PostRegisterOnboarding can pick it up after the login.
  const onboardingParam = params.get('onboarding');
  if (onboardingParam && !currentUser) {
    try { localStorage.setItem('onboarding_link_param', onboardingParam) } catch (_) {}
  }

  // Handle auto-login with token - works even when already logged in
  if (window.location.pathname === '/auth/auto-login') {
    const urlParams = new URLSearchParams(window.location.search);
    const autoLoginToken = urlParams.get('token');
    if (autoLoginToken) {
      // Clear all existing auth
      localStorage.removeItem('kt-auth-react-v');
      localStorage.removeItem('bot_id');
      sessionStorage.clear();
      // Set new token
      localStorage.setItem('kt-auth-react-v', JSON.stringify({api_token: autoLoginToken}));
      // Redirect to campaigns with full page reload
      window.location.replace('/campaigns');
      return null;
    }
  }

  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        {/* Admin routes - separate from main app */}
        <Route path='admin/*' element={<AdminRoutes />} />

        {/* Public rating page - no auth required */}
        <Route path='rate-support' element={<RateSupportPage />} />

        {/* Public customer ticket portal - token-based, no auth */}
        <Route path='my-tickets/:token' element={<CustomerPortal />} />
        <Route path='my-tickets/:token/:ticketId' element={<CustomerPortal />} />

        {/* Public profile-connect — no FBZipper login; spawns bot + QR on open */}
        <Route path='connect/:id' element={<ConnectProfilePage />} />
        {/* New mobile login page (same flow as zubipost.com/app/connect); the previous one stays at /c-classic */}
        <Route path='c/:code' element={<ConnectMobilePage />} />
        <Route path='c-classic/:code' element={<ProfileLoginEmbed />} />

        {/* Public phone photo-upload companion — code-scoped, no login.
            campaigns → posts → photos; /post/:pid deep-links straight to a post */}
        <Route path='m/:code' element={<PostUploadPage />} />
        <Route path='m/:code/campaign/:cid' element={<PostUploadPage />} />
        <Route path='m/:code/post/:pid' element={<PostUploadPage />} />
        {/* Staging bucket for a post still open in the desktop composer (no post id yet) */}
        <Route path='m/:code/draft/:did' element={<PostUploadPage />} />

        {/* Telegram bot account-link confirm page — works logged in or out */}
        <Route path='telegram-connect' element={<TelegramConnect />} />

        {/* Public testimonial-wall landing page — no auth, CTA into /auth/register */}
        <Route path='testimonials' element={<TestimonialsPage />} />

        {/* Public marketing landing page (Hebrew) — register/login open the app */}
        <Route path='landing' element={<LandingPage />} />

        {/* The reels gallery is admin-only now (launch + track as Facebook ads) */}
        <Route path='ads' element={<Navigate to='/admin/ads' replace />} />

        {/* Public animated walkthrough (dummy data + narration) — no auth */}
        <Route path='tutorial-demo' element={<TutorialDemo />} />
        {/* The old video page was replaced by the walkthrough; keep old links working, logged in or out. */}
        <Route path='tutorial-video' element={<Navigate to='/tutorial-demo' replace />} />

        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          {/* Terms and Privacy pages - accessible logged in or out */}
          <Route path='terms' element={<TermsWrapper />} />
          <Route path='privacy' element={<PrivacyWrapper />} />

          {currentUser?.tutorial_gate ? (
            <>
              {/* A new account watches the walkthrough before anything else. */}
              <Route path='welcome-tutorial' element={<TutorialGate />} />
              <Route path='*' element={<Navigate to='/welcome-tutorial' replace />} />
            </>
          ) : currentUser ? (
            <>
              <Route path='/*' element={<PrivateRoutes />} />
              <Route index element={<Navigate to='/campaigns' />} />
            </>
          ) : (
            <>
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='*' element={<Navigate to='/auth' />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
